import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, ExternalLink, Boxes, Pencil, ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { useConfirm } from "@/components/ConfirmDialog";
import { useSchemas } from "@/contexts/SchemaContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (url) => (url ? (url.startsWith("http") ? url : `${BACKEND_URL}${url}`) : "");

export default function Simulations() {
  const [sims, setSims] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", apk_url: "", version: "1.0.0", category: "general", image_url: "", module_ids: [] });
  const [editing, setEditing] = useState(null); // sim object when editing
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const confirmDialog = useConfirm();
  const { modules: mdata } = useSchemas();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/simulations");
      setSims(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleImageUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5 MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    try {
      if (editing) {
        await api.patch(`/simulations/${editing.id}`, form);
        toast.success("Simulation updated");
      } else {
        await api.post("/simulations", form);
        toast.success("Simulation uploaded");
      }
      setOpen(false);
      setEditing(null);
      setForm({ name: "", description: "", apk_url: "", version: "1.0.0", category: "general", image_url: "", module_ids: [] });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name || "",
      description: s.description || "",
      apk_url: s.apk_url || "",
      version: s.version || "1.0.0",
      category: s.category || "general",
      image_url: s.image_url || "",
      module_ids: s.module_ids?.length ? s.module_ids : (s.module_id ? [s.module_id] : []),
    });
    setOpen(true);
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", description: "", apk_url: "", version: "1.0.0", category: "general", image_url: "", module_ids: [] });
    setOpen(true);
  };

  const remove = async (sim) => {
    const ok = await confirmDialog({
      title: "Delete simulation?",
      description: `Permanently remove "${sim.name}" and all its headset assignments. This cannot be undone.`,
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try { await api.delete(`/simulations/${sim.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Module Catalog"
        title="Simulations"
        description="A simulation is the app (APK) installed on the headsets. Linking it to an activity tells the console what the app does, so its difficulty levels, therapies and automatic level changes can find it. The link is matched by activity id (the Module field below)."
        action={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
            <DialogTrigger asChild>
              <Button data-testid="upload-apk-button" onClick={openCreate} className="btn-brand rounded-full">
                <Plus className="h-4 w-4 mr-2" strokeWidth={1.75} /> New Simulation
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-display">{editing ? "Edit Simulation" : "Add Simulation Module"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-slate-600">Name</Label>
                  <Input data-testid="sim-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-slate-600">Version</Label>
                    <Input value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-slate-600">Category (free label, shown under the name)</Label>
                    <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-slate-600">Activity this app runs</Label>
                  <div className="rounded-md border border-input p-2 space-y-1 max-h-40 overflow-y-auto" data-testid="sim-module-select">
                    {(mdata?.modules || []).map((m) => (
                      <label key={m.id} className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={form.module_ids.includes(m.id)}
                          onChange={() => setForm({ ...form, module_ids: form.module_ids.includes(m.id) ? form.module_ids.filter((x) => x !== m.id) : [...form.module_ids, m.id] })} />
                        {m.name || m.id}
                      </label>
                    ))}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">Tick every activity this app contains. Some apps run several activities (for example the voice scenarios); each one is then assigned separately.</div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-slate-600">APK URL</Label>
                  <Input data-testid="sim-apk-url-input" placeholder="https://rediron.com/apks/..." value={form.apk_url} onChange={(e) => setForm({ ...form, apk_url: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-slate-600">Description</Label>
                  <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-slate-600">Cover image</Label>
                  <div className="mt-2 flex items-start gap-3">
                    {form.image_url ? (
                      <div className="relative h-24 w-24 rounded-md overflow-hidden border border-slate-200 bg-slate-50 flex-shrink-0">
                        <img src={imgSrc(form.image_url)} alt="cover" className="h-full w-full object-cover" />
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, image_url: "" })}
                          className="absolute top-1 right-1 h-5 w-5 rounded-full bg-slate-900/80 text-white grid place-items-center hover:bg-red-600"
                          data-testid="sim-image-clear"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ) : (
                      <div className="h-24 w-24 rounded-md border border-dashed border-slate-300 bg-slate-50 grid place-items-center flex-shrink-0">
                        <ImagePlus className="h-5 w-5 text-slate-400" strokeWidth={1.5} />
                      </div>
                    )}
                    <div className="flex-1 space-y-2">
                      <label className="inline-flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-md cursor-pointer hover:bg-slate-50 text-sm">
                        <ImagePlus className="h-4 w-4" strokeWidth={1.75} />
                        {uploading ? "Uploading…" : (form.image_url ? "Replace image" : "Upload image")}
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp,image/gif"
                          className="hidden"
                          onChange={(e) => handleImageUpload(e.target.files?.[0])}
                          data-testid="sim-image-input"
                          disabled={uploading}
                        />
                      </label>
                      <div className="text-[10px] text-slate-500 uppercase tracking-[0.12em]">PNG · JPG · WEBP · max 5 MB</div>
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => { setOpen(false); setEditing(null); }}>Cancel</Button>
                <Button data-testid="sim-submit-button" onClick={submit} className="btn-brand rounded-full">{editing ? "Save" : "Create"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="panel rounded-2xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-purple-50/50">
              <TableHead className="text-xs uppercase tracking-[0.12em] text-slate-500 w-16">Cover</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.12em] text-slate-500">Module</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.12em] text-slate-500">Activity it runs</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.12em] text-slate-500">Version</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.12em] text-slate-500">APK</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.12em] text-slate-500 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-400">Loading…</TableCell></TableRow>
            ) : sims.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-16">
                  <Boxes className="h-8 w-8 text-slate-300 mx-auto mb-2" strokeWidth={1.5} />
                  <div className="text-sm text-slate-500">No simulations yet. Click &quot;New Simulation&quot;.</div>
                </TableCell>
              </TableRow>
            ) : sims.map((s) => (
              <TableRow key={s.id} data-testid={`sim-row-${s.id}`} className="hover:bg-slate-50/60">
                <TableCell>
                  {s.image_url ? (
                    <img src={imgSrc(s.image_url)} alt={s.name} className="h-10 w-10 rounded-md object-cover border border-slate-200" data-testid={`sim-image-${s.id}`} />
                  ) : (
                    <div className="h-10 w-10 rounded-md bg-slate-100 border border-slate-200 grid place-items-center">
                      <Boxes className="h-4 w-4 text-slate-400" strokeWidth={1.5} />
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <div className="font-medium text-slate-900">{s.name}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5" data-testid={`sim-category-${s.id}`}>Category: {s.category || "general"}</div>
                  <div className="text-xs text-slate-500 line-clamp-1">{s.description}</div>
                </TableCell>
                <TableCell data-testid={`sim-activity-${s.id}`}>
                  {(() => {
                    const ids = s.module_ids?.length ? s.module_ids : (s.module_id ? [s.module_id] : []);
                    if (!ids.length) return (
                      <div>
                        <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 text-[10px]">Not linked to an activity</Badge>
                        <div className="text-[10px] text-slate-500 mt-1">Therapies and auto-level can't use it. Edit and tick its activities.</div>
                      </div>
                    );
                    return (
                      <div className="space-y-1.5">
                        {ids.length > 1 && <div className="text-[10px] text-slate-500">Shared app · {ids.length} activities</div>}
                        {ids.map((mid) => {
                          const m = (mdata?.modules || []).find((x) => x.id === mid);
                          return (
                            <div key={mid}>
                              <div className="text-sm font-medium text-slate-900">{m ? m.name : <span className="text-amber-700">{mid} (no longer defined)</span>}</div>
                              {m && <div className="flex flex-wrap items-center gap-1">
                                {(m.domains || []).map((d) => <Badge key={d} variant="outline" className="text-[10px]">{d}</Badge>)}
                                <span className="text-[10px] text-slate-500">{(m.difficultyGuide || []).length ? `5 levels · ${m.difficultyGuide.length} settings each` : "no levels"}</span>
                              </div>}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </TableCell>
                <TableCell className="font-mono text-xs">{s.version}</TableCell>
                <TableCell>
                  <a href={s.apk_url} target="_blank" rel="noopener noreferrer" className="text-sm text-emerald-700 hover:underline inline-flex items-center gap-1">
                    Download <ExternalLink className="h-3 w-3" />
                  </a>
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(s)} data-testid={`sim-edit-${s.id}`}>
                    <Pencil className="h-4 w-4 text-slate-400 hover:text-purple-700" strokeWidth={1.5} />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(s)} data-testid={`sim-delete-${s.id}`}>
                    <Trash2 className="h-4 w-4 text-slate-400 hover:text-red-600" strokeWidth={1.5} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
